// Hold local references to the initialized SDK instances
let firebaseApp = null;
let firebaseAnalytics = null;

/**
 * Lazily initializes Firebase Analytics only when running in a client browser environment.
 * This pattern ensures that Firebase code is split out into a separate chunk by Vite's bundler.
 */
export const initAnalytics = async () => {
  if (typeof window === 'undefined') return null; // Protect against SSR setups if applicable
  if (firebaseAnalytics) return firebaseAnalytics; // Prevent double-initialization

  try {
    // Dynamic imports separate Firebase from your main 'index.html' initial bundle size
    const { initializeApp } = await import('firebase/app');
    const { getAnalytics } = await import('firebase/analytics');

    const firebaseConfig = {
      apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
      authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
      projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
      storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
      appId: process.env.REACT_APP_FIREBASE_APP_ID,
      measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
    };

    firebaseApp = initializeApp(firebaseConfig);
    firebaseAnalytics = getAnalytics(firebaseApp);
    
    return firebaseAnalytics;
  } catch (error) {
    console.error('Firebase Analytics failed to initialize async:', error);
    return null;
  }
};

/**
 * Modern wrapper to log safely across components without worrying if the SDK finished loading.
 * @param {string} eventName - Name of the action (e.g., 'purchase_click')
 * @param {Object} eventParams - Optional structured data payload
 */
export const trackCustomEvent = async (eventName, eventParams = {}) => {
  try {
    const { logEvent } = await import('firebase/analytics');
    const analyticsInstance = await initAnalytics();
    
    if (analyticsInstance) {
      logEvent(analyticsInstance, eventName, eventParams);
    }
  } catch (error) {
    console.error(`Failed logging custom event [${eventName}]:`, error);
  }
};